import {
  BOOSTERS,
  type BoosterCode,
  chestContents,
  LEAGUE,
  LEAGUE_TIERS,
  type LeagueChest,
  leagueChest,
  type LeagueDto,
  type LeagueTier,
} from '@spin/shared';
import { type ReactNode, useEffect, useState } from 'react';
import { clientId, http } from '../api/http';
import { Avatar, Spinner } from '../components/common';
import { Emoji } from '../components/Emoji';
import { CloseIcon } from '../components/icons';
import { BoosterArt, LeagueCup } from '../components/PackArt';
import { reportError, useT } from '../i18n';
import { useSession } from '../store/session';
import { useUi } from '../store/ui';
import { tg } from '../tg/bridge';

const BOOSTER_ORDER: BoosterCode[] = ['x2', 'limit', 'bonus'];

/** The league's trophy: the league's cup on a laurel, in a blue circle. */
function Trophy({ tier }: { tier: LeagueTier }) {
  return (
    <span className="league-trophy">
      <LeagueCup tier={tier} />
    </span>
  );
}

/** A league chest drawn in CSS (red, blue, wooden) or the gift box for the other places. */
export function Chest({ kind }: { kind: LeagueChest | null }) {
  if (!kind) return <span className="chest-none" />;
  if (kind === 'box')
    return (
      <span className="chest-box" aria-hidden>
        <Emoji e="🎁" />
      </span>
    );
  return (
    <span className={`chest chest-${kind}`} aria-hidden>
      <i />
    </span>
  );
}

function BoosterIcon({ code, count }: { code: BoosterCode; count: number }) {
  return (
    <span className={`booster-icon booster-${code}`}>
      <BoosterArt code={code} />
      <i className="booster-count">{count}</i>
    </span>
  );
}

/** A white card over the league (booster details, rules), with the green round close button. */
function Card({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const t = useT();
  return (
    <div className="card-layer" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="white-card" role="dialog">
        <button className="dialog-close" onClick={onClose} aria-label={t('common.close')}>
          <CloseIcon />
        </button>
        <div className="dialog-title">{title}</div>
        {children}
      </div>
    </div>
  );
}

const RULE_PAGES = 4;

/** The four rule pages behind "?", with dots and arrows. */
function Rules({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [page, setPage] = useState(0);
  return (
    <Card title={t('league.rules')} onClose={onClose}>
      <div className="rules-page" data-testid="league-rules">
        <div className="rules-art" aria-hidden>
          <Emoji e={['😎', '🎁', '🏆', '⬆️'][page] ?? ''} />
        </div>
        <p>
          {t(`league.rules_${page + 1}`, {
            cap: LEAGUE.kissCap,
            promote: LEAGUE.promote,
            demote: LEAGUE.demote,
          })}
        </p>
      </div>
      <div className="pager">
        <button disabled={page === 0} onClick={() => setPage(page - 1)} aria-label="‹">
          ◀
        </button>
        {Array.from({ length: RULE_PAGES }, (_, i) => (
          <i key={i} className={i === page ? 'on' : ''} />
        ))}
        <button disabled={page === RULE_PAGES - 1} onClick={() => setPage(page + 1)} aria-label="›">
          ▶
        </button>
      </div>
    </Card>
  );
}

function useHoursLeft(endsAt: number | undefined): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  return endsAt ? Math.max(0, Math.ceil((endsAt - now) / 3_600_000)) : 0;
}

/**
 * The daily league (the trophy in the top bar): a blue card with the league's name, the boosters, the player's
 * group with points and chests, and "Final results in: N hr." at the bottom of the screen.
 */
export function LeagueDialog({ boostersOpen = false }: { boostersOpen?: boolean }) {
  const t = useT();
  const me = useSession((s) => s.me)!;
  const close = useUi.getState().closeDialog;
  const [data, setData] = useState<LeagueDto | null>(null);
  const [showBoosters, setShowBoosters] = useState(boostersOpen);
  const [booster, setBooster] = useState<BoosterCode | null>(null);
  const [rules, setRules] = useState(false);
  const [rewards, setRewards] = useState(false);
  const [busy, setBusy] = useState(false);
  const hours = useHoursLeft(data?.endsAt);

  useEffect(() => tg.backButton.show(close), [close]);
  useEffect(() => {
    http.get<LeagueDto>('/league').then(setData).catch(reportError);
  }, []);

  const use = async (code: BoosterCode) => {
    setBusy(true);
    try {
      setData(await http.post<LeagueDto>('/league/boosters', { booster: code, clientTxId: clientId() }));
      tg.haptic('success');
      useUi.getState().toast(t(`league.used.${code}`), 'success');
      setBooster(null);
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };

  const tier = data ? (LEAGUE_TIERS[data.tier] ?? 'wooden') : 'wooden';
  return (
    <div className="league-layer" onClick={(e) => e.target === e.currentTarget && close()}>
      <div className="league" role="dialog" data-testid="league">
        <button
          className="league-close"
          onClick={close}
          aria-label={t('common.close')}
          data-testid="dialog-close"
        >
          <CloseIcon />
        </button>
        <button className="league-help" onClick={() => setRules(true)} aria-label={t('league.rules')}>
          ?
        </button>
        <div className="league-title">{t(`league.tier.${tier}`)}</div>
        <div className="league-hero">
          <i>{t('league.hint')}</i>
          <button
            className="league-trophy-btn"
            onClick={() => setRewards(true)}
            aria-label={t('league.rewards')}
          >
            <Trophy tier={tier} />
            <span className="info-dot">i</span>
          </button>
          <button
            className={showBoosters ? 'boosters-toggle open' : 'boosters-toggle'}
            onClick={() => setShowBoosters((v) => !v)}
            data-testid="boosters-toggle"
          >
            <span className="tri" aria-hidden>
              ▶
            </span>
            {t('league.boosters')}
          </button>
        </div>
        {showBoosters && data && (
          <div className="boosters-row" data-testid="boosters">
            {BOOSTER_ORDER.map((code) => (
              <button key={code} onClick={() => setBooster(code)} aria-label={t(`booster.${code}`)}>
                <BoosterIcon code={code} count={data.inventory.boosters[code]} />
              </button>
            ))}
          </div>
        )}
        <div className="league-list" data-testid="league-group">
          {!data ? (
            <Spinner />
          ) : data.group.length === 0 ? (
            <p className="league-empty">{t('league.empty')}</p>
          ) : (
            data.group.map((m) => (
              <div key={m.userId} className={m.userId === me.id ? 'league-row mine' : 'league-row'}>
                <Avatar name={m.displayName} photoUrl={m.photoUrl} size={42} />
                <span className="league-name ellipsis">
                  {m.rank}.{m.displayName}
                </span>
                <span className="league-points">
                  <span className="star" aria-hidden>
                    ★
                  </span>
                  <b>{m.points}</b>
                </span>
                <Chest kind={leagueChest(m.rank, m.points)} />
              </div>
            ))
          )}
        </div>
        <div className="league-foot">{t('league.zone_up_hint')}</div>
      </div>
      {data && (
        <div className="final-results" data-testid="league-ends">
          <span aria-hidden>⏱</span> {t('league.final_in', { hours })}
        </div>
      )}
      {booster && data && (
        <Card title={t(`booster.${booster}`)} onClose={() => setBooster(null)}>
          <div className="booster-card" data-testid={`booster-${booster}`}>
            <BoosterIcon code={booster} count={data.inventory.boosters[booster]} />
            <p>{t(`booster.${booster}_desc`, { minutes: BOOSTERS.x2.minutes ?? 5, amount: 10 })}</p>
            {data.me && booster !== 'bonus' && (
              <span className="limit-pill">
                {t('league.until_limit')} <span aria-hidden>★</span>{' '}
                {Math.max(0, data.me.kissCap - data.me.kissPoints)}
              </span>
            )}
            <button
              className="btn btn-green"
              disabled={busy || data.inventory.boosters[booster] < 1}
              onClick={() => void use(booster)}
              data-testid={`use-${booster}`}
            >
              {t('league.activate')}
            </button>
          </div>
        </Card>
      )}
      {rules && <Rules onClose={() => setRules(false)} />}
      {rewards && data && (
        <Card title={t('league.rewards')} onClose={() => setRewards(false)}>
          <div className="rewards-list">
            {(['red', 'blue', 'wooden', 'box'] as LeagueChest[]).map((kind, i) => {
              const c = chestContents(kind, data.tier);
              return (
                <div key={kind} className="reward-row">
                  <Chest kind={kind} />
                  <b>{kind === 'box' ? '4+' : t('league.place', { place: i + 1 })}</b>
                  <span>
                    {kind === 'box'
                      ? t('league.box_contents', { chances: c.chances, points: LEAGUE.boxMinPoints })
                      : t('league.chest_contents', { hearts: c.hearts, chances: c.chances })}
                  </span>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}
